import React, { useState, useEffect } from 'react';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { dailyRecordsAPI } from '../../helpers/api';
import DailyRecordDetailModal from './DailyRecordDetailModal';

interface DailyRecord {
  id: string;
  recordDate: string;
  weatherCondition: string | null;
  temperature: number | null;
  notes: string | null;
  isCompleted: boolean;
  createdAt: string;
  site: {
    id: string;
    name: string;
    project: { name: string };
  };
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { name: string; city: { name: string } };
}

interface SiteDailyRecordsModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  onUpdate?: () => void;
}

const weatherLabels: Record<string, string> = {
  SUNNY: 'Güneşli',
  CLOUDY: 'Bulutlu',
  RAINY: 'Yağmurlu',
  SNOWY: 'Karlı',
  STORMY: 'Fırtınalı',
};

const weatherIcons: Record<string, string> = {
  SUNNY: 'ri-sun-line',
  CLOUDY: 'ri-cloudy-line',
  RAINY: 'ri-rainy-line',
  SNOWY: 'ri-snowy-line',
  STORMY: 'ri-thunderstorms-line',
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  });
};

const SiteDailyRecordsModal: React.FC<SiteDailyRecordsModalProps> = ({
  isOpen,
  toggle,
  site,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [records, setRecords] = useState<DailyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && site) {
      fetchData();
    }
  }, [isOpen, site]);

  const fetchData = async () => {
    if (!site) return;

    try {
      setLoading(true);
      const response = await dailyRecordsAPI.getAll({ siteId: site.id });
      setRecords(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Kayıtlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!site) return null;

  const headerActions = (
    <button
      type="button"
      className="side-modal-expand-btn"
      onClick={toggleExpand}
      title={isExpanded ? 'Küçült' : 'Genişlet'}
    >
      <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
    </button>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Günlük Kayıtlar"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {site.name}
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {site.project.city.name} • {site.project.name}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Son Kayıtlar
              </span>
              <span className="text-muted small">{records.length} kayıt</span>
            </div>
            {records.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">Bu şantiyede henüz günlük kayıt bulunmuyor</p>
              </div>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {records.slice(0, 20).map((record, idx) => (
                  <div
                    key={record.id}
                    className="px-3 py-3"
                    style={{
                      borderBottom: idx < Math.min(records.length, 20) - 1 ? '1px solid #e9ecef' : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedRecordId(record.id);
                      setDetailModalOpen(true);
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ fontWeight: 500 }}>{formatDate(record.recordDate)}</span>
                        </div>
                        <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: '0.85rem' }}>
                          {record.weatherCondition && (
                            <span>
                              <i className={`${weatherIcons[record.weatherCondition]} me-1`}></i>
                              {weatherLabels[record.weatherCondition]}
                            </span>
                          )}
                          {record.temperature !== null && (
                            <span>
                              <i className="ri-temp-hot-line me-1"></i>
                              {record.temperature}°C
                            </span>
                          )}
                        </div>
                        {record.notes && (
                          <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>
                            {record.notes.substring(0, 100)}
                            {record.notes.length > 100 && '...'}
                          </p>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className={`px-2 py-1 rounded-pill small me-2 ${
                            record.isCompleted
                              ? 'bg-success bg-opacity-10 text-success'
                              : 'bg-warning bg-opacity-10 text-warning'
                          }`}
                          style={{ fontWeight: 500, fontSize: '0.75rem' }}
                        >
                          {record.isCompleted ? 'Kilitli' : 'Açık'}
                        </span>
                        <i className="ri-arrow-right-s-line text-muted"></i>
                      </div>
                    </div>
                  </div>
                ))}
                {records.length > 20 && (
                  <div className="px-3 py-2 text-center text-muted" style={{ fontSize: '0.85rem' }}>
                    +{records.length - 20} kayıt daha
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <DailyRecordDetailModal
        isOpen={detailModalOpen}
        toggle={() => setDetailModalOpen(false)}
        recordId={selectedRecordId}
        onUpdate={() => {
          fetchData();
          onUpdate?.();
        }}
      />
    </SideModal>
  );
};

export default SiteDailyRecordsModal;
